import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl,FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    HhsgSharedApplicationsService,
    HhsgSharedClientAdminService,
    HhsgSharedConfigService,
    HhsgSharedCorporationService,
    HhsgSharedFormStateService,
    HhsgSharedStuderEmployeeService,
    HhsgSharedTabControlService,
    HhsgSharedUserAdministratorService,
    HhsgSharedUserAdminService,
    HhsgSharedUserService
} from '../../../services';
import { Constants } from '../../../utilities/constants.util';
import { HhsgSharedTabInterface } from '../../common/tab-control/tab.interface';
import { HhsgSharedEditUserBase } from './edit-user-base.component';

@Component({
    selector: 'hhsg-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss']
})
export class HhsgSharedEditUserComponent extends HhsgSharedEditUserBase implements OnDestroy, OnInit, HhsgSharedTabInterface {
    public readonly emailControlName = "email";
    public readonly employeeIdControlName = "employeeId";
    public readonly firstNameControlName = "firstName";
    public readonly forcePasswordResetControlName = "forcePasswordReset";
    public readonly isLockedOutControlName = "isLockedOut";
    public readonly jobTitleControlName = "jobTitle";
    public readonly hireDateControlName = "hireDate"
    public readonly lastNameControlName = "lastName";
    public readonly ssoNameIdControlName = "ssoNameId";
    public readonly ssoUserControlName = "ssoUser";
    public readonly statusControlName = "status";
    public readonly userRoleControlName = "userRole";

    public readonly isDormantControlName = "isDormant";
    public editEmailEnabled = false;

    constructor(
        public _activatedRoute: ActivatedRoute,
        public _applicationService: HhsgSharedApplicationsService,
        public _clientAdminService: HhsgSharedClientAdminService,
        public _configService: HhsgSharedConfigService,
        private _corporationService: HhsgSharedCorporationService,
        private _employeeService: HhsgSharedStuderEmployeeService,
        private _formStateService: HhsgSharedFormStateService,
        private _router: Router,
        private _tabControlService: HhsgSharedTabControlService,
        private _userAdministratorService: HhsgSharedUserAdministratorService,
        public _userAdminService: HhsgSharedUserAdminService,
        private _userService: HhsgSharedUserService,
        private _formBuilder: UntypedFormBuilder,        
        private datePipe: DatePipe,        
        private _changeDetector: ChangeDetectorRef) {
        super( _activatedRoute,
            _applicationService,
            _clientAdminService,
            _configService,
            _corporationService,
            _employeeService,
            _formStateService,
            _router,
            _tabControlService,
             _userAdministratorService,
             _userAdminService,
             _userService,
             _formBuilder,
             _changeDetector);
    }

    
    initializeFormGroup() {
        this.formGroup = new UntypedFormGroup({
            email: new FormControl(null, [Validators.maxLength(100), Validators.required, Validators.pattern(Constants.EmailRegEx)]),
            employeeId: new FormControl(null, [Validators.maxLength(100)]),
            firstName: new FormControl(null, [Validators.maxLength(100), Validators.required]),
            forcePasswordReset: new FormControl(false),
            isLockedOut: new FormControl(false),
            jobTitle: new FormControl(null, [Validators.maxLength(100)]),
            hireDate: new FormControl(null),
            lastName: new FormControl(null, [Validators.maxLength(100), Validators.required]),
            ssoNameId: new FormControl(null, [Validators.maxLength(100)]),
            ssoUser: new FormControl(true),
            status: new FormControl(false),
            userRole: new FormControl(null, [Validators.required]),
            isDormant: new FormControl(false)
        });
    }

    // This is overridden because it uses the showApplicationDisabled() method to control disabled instead of the boolean from the base class
    protected override addApplicationControl(clientId: number, applicationId: number): void {
        this.formGroup.addControl(
            this.getApplicationControlName(clientId, applicationId),
            this._formBuilderBase.control({ disabled: this.showApplicationDisabled(), value: this.isApplicationChecked(clientId, applicationId) }, null)
        );
    }

    protected override addClientControl(clientId: number): void {
        this.formGroup.addControl(
            this.getClientControlName(clientId),
            this._formBuilderBase.control({ disabled: this.showClientDisabled(), value: this.isClientChecked(clientId) }, null)
        );
    }    

    protected override runExtraValidation() {
        this.validateSsoNameId();
        this.validateEmail();
        this.validateName();
    }

    protected override initializeControls(): void {
        if (this.activeUser) {
            this.formGroup.controls[this.emailControlName].reset({
                disabled: this.isEditing,
                value: this.activeUser.email
            });

            this.formGroup.controls[this.employeeIdControlName].reset({
                disabled: this.showInputControlDisabled(),
                value: this.activeUser.employeeId
            });

            this.formGroup.controls[this.firstNameControlName].reset({
                disabled: this.showInputControlDisabled(),
                value: this.activeUser.firstName
            });

            this.formGroup.controls[this.forcePasswordResetControlName].reset({
                disabled: !this.isEditing,
                value: this.activeUser.shouldResetPassword
            });

            this.formGroup.controls[this.isLockedOutControlName].reset({
                disabled: !this.isEditing,
                value: this.activeUser.isLockedOut
            });

            this.formGroup.controls[this.jobTitleControlName].reset({
                disabled: this.showInputControlDisabled(),
                value: this.activeUser.jobTitle
            });
            
            this.formGroup.controls[this.hireDateControlName].reset({ 
                disabled : false,
                value: this.datePipe.transform(this.activeUser.hireDate,"yyyy-MM-dd")
            });

            this.formGroup.controls[this.lastNameControlName].reset({ 
                disabled: this.showInputControlDisabled(), 
                value: this.activeUser.lastName 
            });

            this.formGroup.controls[this.ssoNameIdControlName].reset({
                disabled: this.showInputControlDisabled(),
                value: this.activeUser.ssoNameId
            });

            this.formGroup.controls[this.ssoUserControlName].reset({
                disabled: this.isLoginMethodNone(),
                value: this.activeUser.isNonSSOUser
            });

            this.formGroup.controls[this.statusControlName].reset({
                disabled: this.showInputControlDisabled(),
                value: this.activeUser.isActive
            });

            this.formGroup.controls[this.isDormantControlName].reset({
                disabled: !this.isEditing,
                value: this.activeUser.isDormant
            });

            this.isShowDormant = this.activeUser.isDormant && this.activeUser.isActive;

            if (this.activeUser.userRole) {
                this.formGroup.controls[this.userRoleControlName].reset({
                    disabled: this.showInputControlDisabled(),
                    value: this.activeUser.userRole.roleName
                });
            }

            if (this._userAdminServiceBase.ClientsWithApplications) {
                this._userAdminServiceBase.ClientsWithApplications.forEach(c => {
                    let control = this.formGroup.controls[this.getClientControlName(c.client.id)];

                    if (control) {
                        control.reset({ disabled: this.showClientDisabled(), value: !this.isAdding && this.isClientChecked(c.client.id) });
                    }
                });

                let applications = this._userAdminServiceBase.ClientsWithApplications.flatMap(c => c.clientApplications);

                if (!this.isAdding && applications && applications.length > 0) {
                    applications.forEach(a => {
                        let control = this.formGroup.controls[this.getApplicationControlName(a.clientId, a.applicationId)];

                        if (control) {
                            control.reset({ disabled: this.showApplicationDisabled(), value: this.isApplicationChecked(a.clientId, a.applicationId) });
                        }
                    })
                }
            }
            if (this.isAdding && this.activeUser.email) {
                this.onEmailExist(this.activeUser.email);
            }
        }
    }

    public isEditableRole(): boolean {
        return this.editableUserRoles?.some(r => r.roleId == this._activeUser?.userRole.roleId);
    }

    protected getIsFormGroupDirty(): boolean {
        return this.formGroup.dirty;
    }

    protected override async loadData(): Promise<void> {
        super.loadData();

        setTimeout(function () {
                document.getElementById('firstName')?.focus();
            }, 1000);
    }

    public onEmailChanged(email: HTMLInputElement): void {
        this.removeError(this.formGroup.controls['email'],'duplicate');
        let emailValue = email.value.trim();

        if (this.activeUser) {
            this.activeUser.email = emailValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.emailControlName].setValue(emailValue);
        this.isEmailValid = false;
    }


    public onEmployeeIdChanged(employeeId: HTMLInputElement): void {
        let employeeIdValue = employeeId.value.trim();

        if (this.activeUser) {
            this.activeUser.employeeId = employeeIdValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.employeeIdControlName].setValue(employeeIdValue);
    }

    public onFirstNameChanged(firstName: HTMLInputElement): void {
        let firstNameValue = firstName.value.trim();

        if (this.activeUser) {
            this.activeUser.firstName = firstNameValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.firstNameControlName].setValue(firstNameValue);
    }

    public onForcePasswordResetChanged(forcePasswordReset: HTMLInputElement): void {
        let forcePasswordResetValue = forcePasswordReset.checked;

        if (this.activeUser) {
            this.activeUser.shouldResetPassword = forcePasswordResetValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.forcePasswordResetControlName].setValue(forcePasswordResetValue);
    }

    public onHireDateChanged(hireDate: any): void {
        let hireDateValue = hireDate.target.value;

        if (this.activeUser) {
            this.activeUser.hireDate = hireDateValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.hireDateControlName].setValue(hireDateValue);
    }

    public onIsLockedOutChanged(isLockedOut: HTMLInputElement): void {
        let isLockedOutValue = isLockedOut.checked;

        if (this.activeUser) {
            if (this.isDormantOriginalValue) {
                this.activeUser.isDormant = isLockedOutValue;
            }

            this.activeUser.isLockedOut = isLockedOutValue;
        }

        this.formGroup.controls[this.isLockedOutControlName].setValue(isLockedOutValue);
        this._formStateServiceBase.isFormDirty = true;
    }

    public onJobTitleChanged(jobTitle: HTMLInputElement): void {
        let jobTitleValue = jobTitle.value.trim();

        if (this.activeUser) {
            this.activeUser.jobTitle = jobTitleValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.jobTitleControlName].setValue(jobTitleValue);
    }

    public onIsDormantChanged(isDormant: HTMLInputElement): void {        
        if (this.activeUser) {
            this.activeUser.isDormant = isDormant.checked;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.isDormantControlName].setValue(isDormant.checked);
    }

    public onLastNameChanged(lastName: HTMLInputElement): void {
        let lastNameValue = lastName.value.trim();

        if (this.activeUser) {
            this.activeUser.lastName = lastNameValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.lastNameControlName].setValue(lastNameValue);
    }

    public onSsoNameIdChanged(ssoNameId: HTMLInputElement): void {
        let ssoNameIdValue = ssoNameId.value.trimEnd();

        if (this.activeUser) {
            this.activeUser.ssoNameId = ssoNameIdValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.ssoNameIdControlName].setValue(ssoNameIdValue);
    }

    public onSsoUserChanged(ssoUser: HTMLInputElement): void {
        let ssoUserValue = ssoUser.value.toLowerCase().trim() === "true";

        if (this.activeUser) {
            this.activeUser.isNonSSOUser = ssoUserValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.ssoUserControlName].setValue(ssoUserValue);
    }

    public onStatusChanged(status: HTMLInputElement): void {
        let statusValue = status.value.toLowerCase().trim() === "true";

        if (this.activeUser) {
            this.activeUser.isActive = statusValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.statusControlName].setValue(statusValue);
    }

    public onUserRoleChanged(userRole: HTMLInputElement): void {
        let userRoleValue = userRole.value;

        if (this.activeUser) {
            this.activeUser.userRole.roleName = userRoleValue;
        }

        this._formStateServiceBase.isFormDirty = true;
        this.formGroup.controls[this.userRoleControlName].setValue(userRoleValue);
    }

    public showApplicationDisabled(): boolean {
        return this.isEditing && !this.isEditableRole();
    }

    public showClientDisabled(): boolean {
        return this.isEditing && !this.isEditableRole();
    }

    public get showEmailError(): boolean {
        return !this.isFormValid() && this.emailErrorMessage > '';
    }

    public get showEmployeeIdError(): boolean {
        return !this.isFormValid() && this.employeeIdErrorMessage > '';
    }

    public get showFirstNameError(): boolean {
        return !this.isFormValid() && this.firstNameErrorMessage > '';
    }

    public showInputControlDisabled(): boolean {
        return this.isEditing && !this.isEditableRole();
    }

    public get showJobTitleError(): boolean {
        return !this.isFormValid() && this.jobTitleErrorMessage > '';
    }

    public get showLastNameError(): boolean {
        return !this.isFormValid() && this.lastNameErrorMessage > '';
    }

    public get showSsoNameIdError(): boolean {
        return !this.isFormValid() && this.ssoNameIdErrorMessage > '';
    }

    public get showStatusError(): boolean {
        return !this.isFormValid() && this.statusErrorMessage > '';
    }

    public get showUserRoleError(): boolean {
        return !this.isFormValid() && this.userRoleErrorMessage > '';
    }

    public get ssoNameIdErrorMessage(): string {
        if (this.doesControlHaveError(this.ssoNameIdControlName, 'maxlength')) {
            return "SSO Name ID is too long.";
        }

        return '';
    }

    public get statusErrorMessage(): string {
        if (this.doesControlHaveError(this.statusControlName, 'required')) {
            return "Status is required.";
        }

        return '';
    }

    public get userRoleErrorMessage(): string {
        if (this.doesControlHaveError(this.userRoleControlName, 'required')) {
            return "User role is required";
        }

        return '';
    }
  
    public get jobTitleErrorMessage(): string {
        if (this.doesControlHaveError(this.jobTitleControlName, 'maxlength')) {
            return "Job Title is too long.";
        }

        return '';
    }

    public get lastNameErrorMessage(): string {
        if (this.doesControlHaveError(this.lastNameControlName, 'required')) {
            return "Last Name is required";
        }
        else if (this.doesControlHaveError(this.lastNameControlName, 'maxlength')) {
            return "Last Name is too long.";
        }

        return '';
    }   

    public get emailErrorMessage(): string {
        if (this.doesControlHaveError(this.emailControlName, 'required')) {
            return "Email is required";
        }
        else if (this.doesControlHaveError(this.emailControlName, 'maxlength')) {
            return "Email is too long.";
        }

        return '';
    }

    public get employeeIdErrorMessage(): string {
        if (this.doesControlHaveError(this.employeeIdControlName, 'maxlength')) {
            return "Employee Id is too long.";
        }

        return '';
    }

    public get firstNameErrorMessage(): string {
        if (this.doesControlHaveError(this.firstNameControlName, 'required')) {
            return "First Name is required";
        }
        else if (this.doesControlHaveError(this.firstNameControlName, 'maxlength')) {
            return "First Name is too long.";
        }

        return '';
    }

    private validateEmail(): void {
        let email = this.formGroup.controls[this.emailControlName].value.trim();

        if (!Constants.EmailRegEx.test(email)) {
            var emailError = "Email address is invalid, please enter a valid email address.";

            if (this.errorMessages.findIndex(x => x === emailError) === -1) {
                this.errorMessages.push(emailError);
            }
        }
    }

    private validateName(): void {
        if (this.activeUser.firstName == undefined || this.activeUser.firstName.trim() == '') {
            this.errorMessages.push("First Name is Required");
        }

        if (this.activeUser.lastName == undefined || this.activeUser.lastName.trim() == '') {
            this.errorMessages.push("Last Name is Required");
        }
    }

    private validateSsoNameId(): void {
        if (this.activeUser.ssoNameId && !Constants.SsoNameRegEx.test(this.activeUser.ssoNameId)) {
            var SsoError = "Please enter a valid SSO Name Id. SSONameId can only contain alphabets, numbers and/or any of these special characters ( @ * ( ) _ - \\ / . )";

            if (this.errorMessages.findIndex(x => x === SsoError) === -1) {
                this.errorMessages.push(SsoError);
            }
        }
    }


    onEmailExist(email: string) {
        let emailvalue = email.trim();
        this.formGroup.get('email').setValue(emailvalue);
        if (this.isAdding == true && !this.formGroup.get('email').errors) {
            
            this._userAdminService.checkEmailExist(emailvalue).subscribe((Existsemail) => {
                if (Existsemail.status == false) {
                    this.formGroup.controls['email'].setErrors({'duplicate': true});
                    this.duplicateEmailError = true;
                }
                else {
                    this.removeError(this.formGroup.controls['email'],'duplicate');
                    this.isEmailValid = true;
                    this.duplicateEmailError = false;
                }
            })
        }
    } 

    removeError(control: AbstractControl, error: string) {
        const err = control.errors; // get control errors
        if (err) {
            delete err[error]; // delete your own error
            if (!Object.keys(err).length) { // if no errors left
            control.setErrors(null); // set control errors to null making it VALID
            } else {
            control.setErrors(err); // controls got other errors so set them back
            }
        }
    }

    toggleEditEmail() {
        this.editEmailEnabled = !this.editEmailEnabled;
        this.setEmailControl();
    }

    setEmailControl() {
        let editEmailStatus = this.checkEditEmailStatus();
        if (editEmailStatus) {
            this.formGroup.controls[this.emailControlName].reset({
                disabled: !this.editEmailEnabled,
                value: this.activeUser.email
            });
        }
    }
}
